import { Order } from '@seatix/shared-domain-order';
import { seasonDateLabel, seasonDisplayName } from '@seatix/shared/utils';

/**
 * What every checkout surface needs to know about the thing being bought.
 *
 * An order is for a single event or for a season pass, never both, and the two
 * carry their identity in different places: an event owns its name, venue and
 * date; a pass borrows them from its dates. Discriminating here is what keeps
 * that difference out of the header, the drawer, the dialogs and the receipt.
 *
 * The dates are shaped per kind on purpose. An event has one moment, so it
 * keeps the badge, the hour and the weekday it has always shown. A pass has N,
 * and no single one of them is honest about the product — it gets the catalog's
 * one-line summary instead.
 */
interface OrderDisplayBase {
  title: string;
  venueName: string | null;
  coverUrl: string | undefined;
  coverAlt: string;
  /**
   * The date as one line, for surfaces with no room for a badge — the checkout
   * header. An event spells its single moment out; a pass summarizes its N.
   */
  dateLine: string;
}

export type OrderDisplay =
  | (OrderDisplayBase & {
      kind: 'event';
      month: string;
      day: string;
      time: string;
      fullDate: string;
    })
  | (OrderDisplayBase & { kind: 'season' })
  | (OrderDisplayBase & {
      kind: 'none';
      venueName: null;
      coverUrl: undefined;
    });

const formatIn = (
  isoDate: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
): string =>
  new Intl.DateTimeFormat('es-MX', { ...options, timeZone: timezone }).format(
    new Date(isoDate),
  );

/**
 * A season pass borrows its venue and date line from its first date, but not
 * its cover: that comes from the pass's own `media[]`, read the same way an
 * event's is.
 * The API rejects a season whose dates disagree on venue, chart, service charge
 * or PSP (seatix-api#1071), so any date answers for the venue — but the dates
 * arrive unordered, so the earliest one is chosen here rather than assumed.
 *
 * An order that carries `season_id` but no `season` block is still a pass, and
 * is what every real pass order looks like until seatix-api#1167 ships. It
 * resolves here to a season with nothing to say rather than to the neutral
 * branch — otherwise the surfaces that only ask *what kind of thing is this*
 * (the expiration dialog's copy) would call a pass an event, which is the one
 * thing they must never do.
 */
function seasonDisplay(order: Order): OrderDisplay | null {
  const season = order.season;
  if (!season) {
    return order.season_id
      ? {
          kind: 'season',
          title: '',
          venueName: null,
          coverUrl: undefined,
          coverAlt: 'Imagen del abono',
          dateLine: '',
        }
      : null;
  }

  const dates = [...(season.events ?? [])].sort((a, b) => {
    const left = Date.parse(a.starts_at);
    const right = Date.parse(b.starts_at);
    return (
      (Number.isNaN(left) ? Number.MAX_SAFE_INTEGER : left) -
      (Number.isNaN(right) ? Number.MAX_SAFE_INTEGER : right)
    );
  });
  const firstDate = dates[0];
  const title = seasonDisplayName(season, firstDate);
  const cover = season.media?.find((media) => media.type === 'cover');

  return {
    kind: 'season',
    title,
    venueName: firstDate?.venue?.name ?? null,
    coverUrl: cover?.url,
    coverAlt: cover?.alt_text || title,
    // A pass with no dates has nothing to summarize; the rest of the card still
    // renders, which is the point of not throwing here.
    dateLine: firstDate ? seasonDateLabel(dates, firstDate) : '',
  };
}

function eventDisplay(order: Order): OrderDisplay | null {
  const event = order.event;
  if (!event) return null;

  const timezone = event.venue?.timezone || 'UTC';
  // An unparseable date is treated as no date at all. `formatIn` would throw a
  // `RangeError` on it, and this adapter is the single funnel for the header,
  // the countdown, the drawer and the receipt — one bad `starts_at` would take
  // all four down, the very failure this issue exists to close. The season path
  // guards the same thing inside `seasonDateLabel`.
  const startsAt =
    event.starts_at && !Number.isNaN(Date.parse(event.starts_at))
      ? event.starts_at
      : '';
  const title = event.name ?? '';

  return {
    kind: 'event',
    title,
    venueName: event.venue?.name ?? null,
    coverUrl: event.cover_image?.url,
    coverAlt: event.cover_image?.alt_text || title || 'Imagen del evento',
    month: startsAt ? formatIn(startsAt, timezone, { month: 'short' }) : '',
    day: startsAt ? formatIn(startsAt, timezone, { day: '2-digit' }) : '',
    time: startsAt
      ? formatIn(startsAt, timezone, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        })
      : '',
    fullDate: startsAt
      ? `${formatIn(startsAt, timezone, { weekday: 'long' })}, ${formatIn(
          startsAt,
          timezone,
          { month: 'long' },
        )} ${formatIn(startsAt, timezone, { day: 'numeric' })}`
      : '',
    dateLine: startsAt
      ? `${formatIn(startsAt, timezone, { weekday: 'long' })} ${formatIn(
          startsAt,
          timezone,
          { hour: '2-digit', minute: '2-digit', hour12: true },
        )}, ${formatIn(startsAt, timezone, { day: '2-digit' })}/${formatIn(
          startsAt,
          timezone,
          { month: 'long' },
        )}/${formatIn(startsAt, timezone, { year: 'numeric' })}`
      : '',
  };
}

/**
 * What a surface renders when there is no order yet, or when the order names
 * neither an event nor a pass. Every field is blank rather than absent, so a
 * template can bind it without guarding each one.
 */
export const EMPTY_ORDER_DISPLAY: OrderDisplay = {
  kind: 'none',
  title: '',
  venueName: null,
  coverUrl: undefined,
  coverAlt: 'Imagen del evento',
  dateLine: '',
};

/**
 * The one place the checkout decides what an order is for. Every surface reads
 * this instead of `order.event`, which is absent on a season pass.
 *
 * `kind: 'none'` is the order that names neither — no `event`, no `season` and
 * no `season_id`. It is not how a pass looks before seatix-api#1167 ships: that
 * order still carries `season_id`, so it resolves to a season with nothing to
 * say. Rendering blank beats crashing.
 */
export function orderDisplay(order: Order): OrderDisplay {
  return eventDisplay(order) ?? seasonDisplay(order) ?? EMPTY_ORDER_DISPLAY;
}
